import { inject, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import flatpickr from 'flatpickr';
import { DatepickerDirective } from '../../common/directives/datepicker.directive';
import { LOG } from '../../common/util/log';
import { SymbolTimestamps, SymbolZoom, ZoomInfo } from '../charts/chart-transformers';
import { ChartBinding } from './chart-controller.directive';
import { CHART_ZOOM, CURRENT_CHART_TIMEFRAME } from './chart-timeframe-controller.directive';
import { SelectedTimeframe } from './selected-timeframe';

const dayDuration: number = 24 * 60 * 60 * 1000;

export interface ChartTimeframe {
  from: number;
  to: number;
  fromStr: string;
  toStr: string;
}

export type ChartTimeframes = {
  [K in `${SelectedTimeframe}`]: ChartTimeframe;
};

export class ChartTimeframeSelector {
  public static readonly EVENT_TRIGGER = 'timeframeSelector';

  public timesframes?: ChartTimeframes;

  private chartBinding: ChartBinding | undefined;
  private chartSignal: Signal<Highcharts.Chart | undefined> | undefined;
  private initialized: boolean = false;

  private animate: boolean = true;

  private readonly trackedSelection: WritableSignal<ChartTimeframe | null> = inject(CURRENT_CHART_TIMEFRAME);
  private readonly zoom = inject(CHART_ZOOM, { optional: true });

  private get eventArgs() {
    return {
      trigger: ChartTimeframeSelector.EVENT_TRIGGER,
    };
  }

  private get chart(): Highcharts.Chart {
    const chart: Highcharts.Chart | undefined = this.chartSignal?.() ?? this.chartBinding.chart?.();

    if (chart) {
      return chart;
    }

    throw new Error('No chart provided for timeframe selector.');
  }

  constructor() {
    this.zoom?.pipe(takeUntilDestroyed()).subscribe(zoom => {
      const timeframe: ChartTimeframe = this.getTimeframeFromZoom(zoom[1]);

      if (timeframe) {
        this.updateTrackedSelection(timeframe);
      }
    });
  }

  public initialize(chartSource: Signal<Highcharts.Chart | undefined> | ChartBinding) {
    this.initialized = true;
    if (typeof chartSource === 'function') {
      this.chartSignal = chartSource;
    } else {
      this.chartBinding = chartSource;
    }
  }

  public updateSelection(selection: SelectedTimeframe, animate: boolean = true): void {
    if (!this.initialized) {
      LOG.error('Cant update chart timeframe selection. Timeframe selector is uninitialized.');
      return;
    }

    const animateBackup: boolean = this.animate;
    this.animate = animate;

    try {
      this.displayPreselectedTimeframe(selection);
      this.updateTrackedSelection(selection);
    } finally {
      this.animate = animateBackup;
    }
  }

  public updateRange(range: ChartTimeframe, animate: boolean = true) {
    if (!this.initialized) {
      LOG.error('Cant update chart timeframe range. Timeframe selector is uninitialized.');
      return;
    }

    const animateBackup: boolean = this.animate;
    this.animate = animate;

    try {
      this.displayTimeframe(range);
      this.updateTrackedSelection(range);
    } finally {
      this.animate = animateBackup;
    }
  }

  private displayPreselectedTimeframe(selection: SelectedTimeframe): void {
    if (!this.timesframes) {
      LOG.error('No timeframes provided for timeframe selector.');
      return;
    }

    const timeframe: ChartTimeframe | undefined = this.timesframes?.[selection];

    if (!timeframe) {
      LOG.error(`Unknown timeframe selected: "${selection}"`);
      return;
    }

    this.displayTimeframe(timeframe);
  }

  private displayTimeframe(timeframe: ChartTimeframe): void {
    const padding: number = 0.25;
    const chart: Highcharts.Chart = this.chart;

    for (const xAxis of chart.xAxis) {
      xAxis.setExtremes(
        -padding * dayDuration + timeframe.from,
        padding * dayDuration + timeframe.to,
        true,
        this.animate,
        this.eventArgs
      );
    }

    chart.options[SymbolZoom] = <ZoomInfo>{ start: timeframe.from, end: timeframe.to };
  }

  private getTimeframeFromZoom(event: Highcharts.AxisSetExtremesEventObject): ChartTimeframe {
    const chart: Highcharts.Chart | undefined = this.chart;

    if (event.trigger === ChartTimeframeSelector.EVENT_TRIGGER) {
      return null;
    }

    const extremes: Highcharts.ExtremesObject = event;
    const timestamps: number[] = chart.options[SymbolTimestamps];

    const timestampFrom: number = extremes.userMin !== undefined ? Math.floor(extremes.userMin) : timestamps[0];
    const timestampTo: number =
      extremes.userMax !== undefined ? Math.floor(extremes.userMax) : timestamps[timestamps.length - 1];

    const dateFrom: Date = this.roundDate(timestampFrom);
    const dateTo: Date = this.roundDate(timestampTo);

    dateFrom.setUTCDate(dateFrom.getUTCDate() + 1);

    return {
      from: dateFrom.getTime(),
      fromStr: flatpickr.formatDate(dateFrom, DatepickerDirective.DATA_FORMAT),
      to: dateTo.getTime(),
      toStr: flatpickr.formatDate(dateTo, DatepickerDirective.DATA_FORMAT),
    };
  }

  private roundDate(timestamp: number): Date {
    const date = new Date(timestamp);
    date.setUTCHours(0);
    date.setUTCMinutes(0);
    date.setUTCSeconds(0, 0);
    return date;
  }

  private updateTrackedSelection(selection: ChartTimeframe | SelectedTimeframe): void {
    if (typeof selection === 'string') {
      this.trackedSelection.set(this.timesframes[selection]);
    } else {
      this.trackedSelection.set(selection);
    }
  }
}
